import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomerDetails from "./components/CustomerDetails";
import {
  isFormValid,
  validateGuestForm,
} from "../../../../../Utils/validations";
import PastStays from "./components/PastStays";
import PaymentHistory from "./components/PaymentHistory";
import FormCard from "../../../../../Lib/FormCard";
import { toast } from "react-toastify";
import { ScaleLoader } from "react-spinners";
import { getBaseUrl } from "../../../../../Redux/Config/AppConfig";
import { hasEditAccess } from "../../../../../Common/CheckAccess";

const emptyGuest = {
  countryCode: "",
  mobileNo: "",
  first_name: "",
  last_name: "",
  fatherName: "",
  gender: "",
  occupation: "",
  dob: "",
  email: "",
  country: "",
  state: "",
  city: "",
  zipcode: "",
  address: "",
  identityType: "",
  idNumber: "",
  comments: "",
  isVIP: false,
  agent_name: "",
  booking_source: "",
  nationality: "",
};

const RoomBooking = () => {
  const location = useLocation();
  const { userId, mode } = location.state || {};
  const isCreateMode = mode === "create";

  const [mainGuest, setMainGuest] = useState({
    data: { ...emptyGuest },
    errors: {},
    isValid: true,
  });

  const mainGuestRefs = {
    first_name: useRef(null),
    last_name: useRef(null),
    mobileNo: useRef(null),
    email: useRef(null),
  };

  const [additionalGuests, setAdditionalGuests] = useState([]);
  const [loading, setLoading] = useState(!!userId);
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("stays");
  const [stayHistoryData, setStayHistoryData] = useState({});
  const [paymentHistoryData, setPaymentHistoryData] = useState({});
  const navigate = useNavigate();

  const [permissions, setPermissions] = useState([]);

  const existingPermissions = localStorage.getItem("checkPermission");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);

  const scrollToFirstError = () => {
    const errorKeys = Object.keys(mainGuest.errors);
    for (const key of errorKeys) {
      if (mainGuest.errors[key] && mainGuestRefs[key]?.current) {
        mainGuestRefs[key].current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        mainGuestRefs[key].current.focus();
        break;
      }
    }
  };

  useEffect(() => {
    if (userId) {
      fetchGuestData(userId);
    }
  }, [userId]);

  const BaseUrl = getBaseUrl();

  const fetchGuestData = async (id) => {
    try {
      setLoading(true);
      let token = localStorage.getItem("token");

      const response = await fetch(`${BaseUrl}/guest/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      const guestData = data?.data?.guestData;
      const guestDetails = data?.data?.guestDetails;
      setStayHistoryData(data?.data?.reservations);
      // setPaymentHistoryData(data?.data?.paymentDetails);
      if (guestData) {
        setMainGuest({
          data: {
            countryCode: guestData.country_code || "",
            mobileNo: guestData.phone || "",
            first_name: guestData.first_name || "",
            last_name: guestData.last_name || "",
            fatherName: guestData.father_name || "",
            agent_name: guestData.agent_name || "",
            booking_source: guestData.booking_source || "",
            gender: guestData.gender || "",
            occupation: guestData.occupation || "",
            dob: guestData.date_of_birth || "",
            email: guestData.email || "",
            nationality: guestData.nationality || "",
            state: guestDetails.state || "",
            city: guestDetails.city || "",
            zipcode: guestDetails.zip_code || "",
            address: guestDetails.address || "",
            identityType: guestDetails.document_type || "",
            frontend_url: guestDetails?.frontend_url || "",
            idNumber: guestData.idNumber || "",
            comments: guestDetails.comment || "",
            isVIP: guestData.isVIP || false,
            country: guestDetails.country || "India",
          },
          errors: {},
          isValid: true,
        });
      }
    } catch (error) {
      console.error("Error fetching guest data:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateMainGuest = () => {
    const errors = validateGuestForm(mainGuest.data);
    setMainGuest((prev) => ({
      ...prev,
      errors,
      isValid: isFormValid(errors),
    }));
  };

  const validateAdditionalGuests = () => {
    const validated = additionalGuests.map((guest) => {
      const errors = validateGuestForm(guest.data);
      return {
        ...guest,
        errors,
        isValid: isFormValid(errors),
      };
    });
    setAdditionalGuests(validated);
  };

  const updateMainGuestData = (name, value) => {
    setMainGuest((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: value,
      },
    }));
  };

  const updateAdditionalGuestData = (index, name, value) => {
    setAdditionalGuests((prev) => {
      const updated = [...prev];
      const updatedData = {
        ...updated[index].data,
        [name]: value,
      };
      const errors = validateGuestForm(updatedData);

      updated[index] = {
        data: updatedData,
        errors,
        isValid: isFormValid(errors),
      };

      return updated;
    });
  };

  const toggleEditMode = () => {
    if (isEditing) {
      setIsEditing(false);
      setAdditionalGuests([]);
    } else {
      setIsEditing(true);
    }
  };

  const addGuest = () => {
    setAdditionalGuests((prev) => [
      ...prev,
      {
        data: { ...emptyGuest },
        errors: {},
        isValid: false,
      },
    ]);
  };

  const removeGuest = (index) => {
    setAdditionalGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    console.log("save click");
    validateMainGuest();
    const isEmpty =
      !mainGuest.data.first_name.trim() ||
      !mainGuest.data.mobileNo.trim() ||
      !mainGuest.data.email.trim();
    const isPhoneInvalid = !/^\d{10}$/.test(mainGuest.data.mobileNo);
    const isEmailInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      mainGuest.data.email
    );

    if (isEmpty || isPhoneInvalid || isEmailInvalid || !mainGuest.isValid) {
      scrollToFirstError();
      return;
    }

    try {
      setIsSaving(true);

      const formData = new FormData();

      const {
        first_name,
        last_name,
        fatherName,
        gender,
        occupation,
        dob,
        email,
        mobileNo,
        countryCode,
        country,
        state,
        city,
        zipcode,
        address,
        identityType,
        comments,
        document,
        agent_name,
        booking_source,
        nationality,
      } = mainGuest.data;

      formData.append("firstName", first_name || "");
      formData.append("lastName", last_name || "");
      formData.append("fatherName", fatherName || "");
      formData.append("agent_name", agent_name || "");
      formData.append("booking_source", booking_source || "");
      formData.append("gender", gender || "");
      formData.append("occupation", occupation || "");
      formData.append("date_of_birth", dob || "");
      formData.append("email", email || "");
      formData.append("phone", mobileNo || "");
      formData.append("country_code", countryCode || "");
      formData.append("nationality", nationality || "");
      formData.append("country", country);
      formData.append("state", state || "");
      formData.append("city", city || "");
      formData.append("zip_code", zipcode || "");
      formData.append("address", address || "");
      formData.append("document_type", identityType || "");
      formData.append("comment", comments || "");
      let hotel_id = localStorage.getItem("hotel_id");

      formData.append("hotel_id", hotel_id || "");

      if (document instanceof File) {
        formData.append("document", document);
      }
      let token = localStorage.getItem("token");

      const response = await fetch(`${BaseUrl}/guest/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (result?.success) {
        toast.success(result?.message);
        navigate("/front-desk/guest-management");
        setIsSaving(false);
      }
      if (!response.ok) {
        setIsSaving(false);
        throw new Error(result.message || "Guest creation failed");
      }

      //   setIsEditing(false);
      setAdditionalGuests([]);
    } catch (error) {
      console.error("Error saving guest:", error);
      toast.error(error.message, "Err msg");
      setIsSaving(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSave = async () => {
    validateMainGuest();
    const isEmpty =
      !mainGuest.data.first_name.trim() ||
      !mainGuest.data.mobileNo.trim() ||
      !mainGuest.data.email.trim();
    const isPhoneInvalid = !/^\d{10}$/.test(mainGuest.data.mobileNo);
    const isEmailInvalid = !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      mainGuest.data.email
    );
    if (isEmpty || isPhoneInvalid || isEmailInvalid) {
      return;
    }

    try {
      setIsSaving(true);

      const formData = new FormData();

      const {
        first_name,
        last_name,
        fatherName,
        agent_name,
        booking_source,
        gender,
        occupation,
        dob,
        email,
        mobileNo,
        countryCode,
        country,
        state,
        city,
        zipcode,
        address,
        identityType,
        comments,
        document,
      } = mainGuest.data;

      formData.append("firstName", first_name || "");
      formData.append("lastName", last_name || "");
      formData.append("fatherName", fatherName || "");
      formData.append("agent_name", agent_name || "");
      formData.append("booking_source", booking_source || "");
      formData.append("gender", gender || "");
      formData.append("occupation", occupation || "");
      formData.append("date_of_birth", dob || "");
      formData.append("email", email || "");
      formData.append("phone", mobileNo || "");
      formData.append("country_code", countryCode || "");
      formData.append("nationality", country || "");
      formData.append("state", state || "");
      formData.append("city", city || "");
      formData.append("zip_code", zipcode || "");
      formData.append("address", address || "");
      formData.append("document_type", identityType || "");
      formData.append("comment", comments || "");

      if (document instanceof File) {
        formData.append("document", document);
      }
      let token = localStorage.getItem("token");

      const response = await fetch(`${BaseUrl}/guest/update/${userId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();
      if (result?.success) {
        toast.success(result?.message);
        navigate("/front-desk/guest-management");
      }
      if (!response.ok) {
        throw new Error(result.message || "Guest updation failed");
      }

      setIsEditing(false);
      setAdditionalGuests([]);
    } catch (error) {
      console.error("Error saving guest:", error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const getPaymentHistory = async () => {
      let token = localStorage.getItem("token");
      if (userId) {
        const historyData = await fetch(
          `${BaseUrl}/billing/payments/guest/${userId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const response = await historyData.json();
        setPaymentHistoryData(response?.data?.payments);
      }
    };
    getPaymentHistory();
  }, []);

  const isFormDataValid = () =>
    mainGuest.isValid && additionalGuests.every((guest) => guest.isValid);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <ScaleLoader />
      </div>
    );
  }

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3">
        <div className="col-sm-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap">
            <h3 className="fw-bold mb-0">Guest Details</h3>
            <div className="d-flex align-content-center gap-3">
              <button
                type="button"
                className="btn btn-secondary"
                style={{ maxHeight: "40px" }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Back
              </button>
              {!isCreateMode && hasEditAccess(permissions, "front_desk") && (
                <button
                  type="button"
                  className={`btn ${
                    isEditing ? "btn-outline-danger" : "btn-outline-primary"
                  } btn-sm d-flex align-items-center gap-1`}
                  onClick={toggleEditMode}
                  disabled={isSaving}
                >
                  <i
                    className={`bi ${isEditing ? "bi-x-circle" : "bi-pencil"}`}
                  ></i>
                  <span>{isEditing ? "Cancel" : "Edit"}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="col-sm-12 gap-3">
          <CustomerDetails
            formData={mainGuest.data}
            updateFormData={updateMainGuestData}
            errors={mainGuest.errors}
            disabled={!(isEditing || isCreateMode)}
            refs={mainGuestRefs}
          />
        </div>

        {isEditing &&
          additionalGuests.map((guest, index) => (
            <div className="col-sm-12 mb-3" key={index}>
              <CustomerDetails
                formData={guest.data}
                updateFormData={(name, value) =>
                  updateAdditionalGuestData(index, name, value)
                }
                errors={guest.errors}
                disabled={false}
                index={index + 1}
                onRemove={() => removeGuest(index)}
                showRemoveButton={true}
              />
            </div>
          ))}

        <div className="col-sm-12 text-end">
          {isEditing && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                !isCreateMode ? handleEditSave() : handleSave();
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          )}
        </div>

        {/* <div className="col-sm-12 text-end">
          {isEditing && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleEditSave}
              disabled={isSaving}
            >
                Add Room
            </button>
          )}
        </div> */}

        {!isCreateMode && (
          <div className="card w-100 mb-3">
            <div className="card-header border-bottom-0 pb-0">
              <ul className="nav nav-tabs border-bottom">
                <li className="nav-item">
                  <button
                    className={`nav-link fw-bold`}
                    style={{
                      color: activeTab === "stays" ? "#E05170" : "#000",
                      borderBottom:
                        activeTab === "stays"
                          ? "3px solid #E05170"
                          : "3px solid transparent",
                      backgroundColor: "transparent",
                    }}
                    onClick={() => setActiveTab("stays")}
                  >
                    Past Stays
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link fw-bold duration-700`}
                    style={{
                      color: activeTab === "payments" ? "#E05170" : "#000",
                      borderBottom:
                        activeTab === "payments"
                          ? "3px solid #E05170"
                          : "3px solid transparent",
                      backgroundColor: "transparent",
                    }}
                    onClick={() => setActiveTab("payments")}
                  >
                    Payment History
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {activeTab === "stays" && <PastStays stays={stayHistoryData} />}
              {activeTab === "payments" && (
                <PaymentHistory payments={paymentHistoryData} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomBooking;
